<template>
  <div class="ski-run-field">
    <label :for="id">{{ label }}</label>
    
    <!-- Text input for manual ski run entry when it's "No Observation" -->
    <input 
      v-if="isNoObservation"
      type="text"
      :id="id"
      v-model="skiRunValue"
      :placeholder="fieldPlaceholder"
      :disabled="true"
      @input="updateValue"
      class="ski-run-input"
    />
    
    <!-- Combined input/dropdown approach for wildlife observations -->
    <div v-else class="combined-input">
      <!-- Always show text input for manual entry -->
      <input 
        type="text"
        :id="id"
        v-model="skiRunValue"
        :placeholder="fieldPlaceholder"
        @input="updateValue"
        class="ski-run-input"
      />
      
      <!-- Show dropdown as a suggestion helper if a single zone is selected -->
      <select 
        v-if="hasSingleZoneWithRuns"
        @change="handleRunSelect"
        class="run-suggestions"
      >
        <option disabled value="" selected>Select from available runs</option>
        <option v-for="run in runsForSelectedZone" :key="run.runid" :value="run.runname">
          {{ run.runname }}
        </option>
      </select>
    </div>
    
    <div v-if="validationMessage" class="validation-message">
      {{ validationMessage }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'SkiRunField',
  props: {
    value: {
      type: String,
      default: ''
    },
    label: {
      type: String,
      default: 'Ski Run'
    },
    id: {
      type: String,
      default: 'ski-run'
    },
    selectedZones: {
      type: Array,
      default: () => []
    },
    runs: {
      type: Array,
      default: () => []
    },
    species: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      skiRunValue: this.value,
      validationMessage: ''
    };
  },
  computed: {
    isNoObservation() {
      return this.species === 'No Observation';
    },
    hasSingleZoneWithRuns() {
      return this.selectedZones.length === 1 && this.runsForSelectedZone.length > 0;
    },
    runsForSelectedZone() {
      if (this.selectedZones.length !== 1) return [];
      
      const zoneId = this.selectedZones[0].id;
      return this.runs.filter(run => run.zoneid === zoneId);
    },
    fieldPlaceholder() {
      if (this.isNoObservation) {
        return 'Not required for No Observation';
      } else if (this.selectedZones.length === 0) {
        return 'Enter ski run or select from dropdown';
      } else if (this.selectedZones.length === 1) {
        return 'Enter ski run or select from dropdown';
      } else {
        return 'Enter ski run';
      }
    }
  },
  watch: {
    value(newVal) {
      this.skiRunValue = newVal;
    },
    species(newVal) {
      // Clear ski run value when species changes to "No Observation"
      if (newVal === 'No Observation') {
        this.skiRunValue = '';
        this.updateValue();
      }
      
      // Validate when species changes
      this.validate();
    },
    selectedZones() {
      // Don't clear the ski run value when zones change
      // This allows the ski run field to work independently from zone selection
      
      // Still validate when selected zones change
      this.validate();
    }
  },
  methods: {
    updateValue() {
      this.$emit('input', this.skiRunValue);
      this.validate();
    },
    handleRunSelect(event) {
      // Set the selected run from the dropdown to the input field
      this.skiRunValue = event.target.value;
      this.updateValue();
      
      // Reset the dropdown to the placeholder option
      event.target.value = '';
    },
    validate() {
      this.validationMessage = '';

      // Ski run is now optional for all reports
      // No validation needed

      return true;
    }
  }
};
</script>

<style scoped>
.ski-run-field {
  margin-bottom: 10px;
}

label {
  display: block;
  margin-bottom: 5px;
  font-weight: bold;
}

.ski-run-input,
.run-suggestions {
  width: 100%;
  padding: 5px;
  border: 1px solid #ccc;
  border-radius: 3px;
}

.combined-input {
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.validation-message {
  color: red;
  font-size: 0.8em;
  margin-top: 3px;
}
</style>